import $ from 'jquery';
import _ from 'underscore';
import { $window, jqGetElemById, isMobile, jqOffOn, setScrollPosition } from 'util/global';
import { track } from 'util/analytics';
import {
  CLASS_NAME_ACTIVE,
  CLASS_NAME_ANIM_IN,
  CLASS_NAME_HIDE,
  INTERACTION_TYPES,
} from 'util/constants';
import { CODEX_LINE_HEIGHT } from 'app/codex/constants';

const CODEX_NOTE_EDIT_BTN_WIDTH = 18;
const CODEX_NOTE_EDIT_BTN_HEIGHT = CODEX_LINE_HEIGHT;
const CODEX_NOTE_EDIT_BTN_SPACING = 4;

const CODEX_NOTE_INDEX_START = -1;

/**
 * @typedef {object} CodexNoteInfo
 * @property {string} content - The actual note content
 * @property {array} lines - A range of lines to highlight
 * @property {boolean} expect_user_to_edit - Flag for an editable line
 * @property {number} show_on_solvable_id - Only show the note on certain problems
 */

/**
 * Get the classes for a codex editor button
 * @param {string} btnType - Some string to indicate a different type of button, e.g. 'edit'
 * @returns {string}
 */
function classForButtonType(btnType) {
  return `btn codex-note-btn codex-note-${btnType}`;
}

export class CodexNoteEditor {
  /**
   * @param {object} opts
   * @param {CodeMirror} cm
   * @param {JQuery} $notesElement
   */
  constructor({ cm, $notesElement }) {
    this.cm = cm;
    this.$notesElement = $notesElement;
    this.existingNoteButtons = [];
    this.readData();
    this.renderNoteButtons();
    this.bindEvents();
    this.handleSecondaryInputs();
  }

  /** Load note data from the notes input field */
  readData() {
    let data;
    try {
      data = JSON.parse(this.$notesElement.val());
    } catch (e) {
      data = {};
    }

    this.notes = data.notes || [];
    this.showIntro = data.show_intro || false;
  }

  /** Serialize local data and add back to the notes input field */
  saveData() {
    const data = {
      notes: this.notes,
    };

    if (this.showIntro) {
      data.show_intro = true;
    }

    this.$notesElement.val(JSON.stringify(data, null, 2));
  }

  /**
   * Insert the note object at `noteNumber`
   * @param {number} noteNumber
   * @param {CodexNoteInfo} noteInfo
   */
  saveNote(noteNumber, noteInfo) {
    const lines = [];
    const lineRange = [parseInt(noteInfo.startLine, 10), parseInt(noteInfo.endLine, 10)].sort();
    let lineNumber = lineRange[0];
    do {
      lines.push(lineNumber - 1);
      lineNumber++;
    } while (lineNumber <= lineRange[1]);

    const noteData = {
      expect_user_to_edit: noteInfo.expect_user_to_edit,
      content: noteInfo.content,
      lines,
    };

    if (noteInfo.show_on_solvable_id) {
      noteData.show_on_solvable_id = parseInt(noteInfo.show_on_solvable_id, 10);
    }

    const targetNoteNumber = parseInt(noteInfo.noteNumber, 10);
    if (noteNumber !== targetNoteNumber) {
      this.notes.splice(noteNumber - 1, 1);
      this.notes.splice(targetNoteNumber - 1, 0, noteData);
    } else {
      this.notes.splice(noteInfo.noteNumber - 1, 1, noteData);
    }

    this.saveData();
    this.renderNoteButtons();
  }

  /**
   * Remove the note at `noteNumber`
   * @param {number} noteNumber
   */
  deleteNote(noteNumber) {
    this.notes.splice(noteNumber - 1, 1);
    this.saveData();
    this.renderNoteButtons();
  }

  /** Render edit buttons for each note */
  renderNoteButtons() {
    const scrollIntoView = false;
    const $baseButton = $(document.createElement('button'));
    $baseButton.addClass(classForButtonType('edit'));
    $baseButton.css({
      width: CODEX_NOTE_EDIT_BTN_WIDTH,
    });

    this.existingNoteButtons.forEach($btn => $btn.remove());
    this.existingNoteButtons = [];

    this.notesPerLine = {};
    this.notes.forEach((noteInfo, index) => {
      const $btn = $baseButton.clone();
      $btn.text(`${index + 1}`);

      $btn.on('click', e => {
        e.preventDefault();
        this.showNoteEditModal(noteInfo, index + 1);
      });

      this.cm.addWidget(
        {
          line: noteInfo.lines[0],
          ch: 0,
        },
        $btn[0],
        scrollIntoView,
      );

      const rightOffset = CODEX_NOTE_EDIT_BTN_WIDTH + CODEX_NOTE_EDIT_BTN_SPACING;
      $btn.css(this.getButtonStylesForLineSet(noteInfo.lines, rightOffset));
      this.existingNoteButtons.push($btn);

      noteInfo.lines.forEach(lineNumber => {
        this.notesPerLine[lineNumber] = this.notesPerLine[lineNumber] || 0;
        this.notesPerLine[lineNumber]++;
      });
    });
  }

  /** Attach events for note editor */
  bindEvents() {
    const $addNoteButton = $(document.createElement('button'));
    $addNoteButton.addClass(`${classForButtonType('add')} btn-accent`);
    $addNoteButton.text('+');

    this.cm.on('cursorActivity', () => {
      const selection = this.cm.doc.listSelections()[0];
      const selectedLinesInfo = [selection.anchor.line, selection.head.line].sort();
      const selectedLines = [];
      let lineNumber = selectedLinesInfo[0];
      do {
        selectedLines.push(lineNumber);
        lineNumber++;
      } while (lineNumber <= selectedLinesInfo[1]);

      jqOffOn($addNoteButton, 'click', e => {
        e.preventDefault();
        this.showNoteEditModal({
          lines: selectedLines,
        });
      });

      $addNoteButton.css(this.getButtonStylesForLineSet(selectedLines));

      const scrollIntoView = true;
      this.cm.addWidget({ line: selectedLines[0], ch: 0 }, $addNoteButton[0], scrollIntoView);
    });
  }

  /** Handler for secondary note form inputs (e.g. should show intro?) */
  handleSecondaryInputs() {
    const $showIntroInput = jqGetElemById('codex-notes-show-intro');

    $showIntroInput.on('change', () => {
      this.showIntro = $showIntroInput.prop('checked');
      this.saveData();
    });

    $showIntroInput.prop('checked', this.showIntro);
  }

  /**
   * Render the note editor template for a given note and show in a modal
   * @param {CodexNoteInfo} noteInfo
   * @param {number} noteNumber
   */
  showNoteEditModal(noteInfo, noteNumber) {
    this.template = this.template || _.template(jqGetElemById('codex-note-edit-modal').text());

    const maxNoteNumber = this.notes.length + 1;
    const thisNoteNumber = noteNumber || maxNoteNumber;
    const data = {
      show_on_solvable_id: '',
      expect_user_to_edit: false,
      content: '',
      isEditing: noteInfo.content && noteInfo.content.length > 0,
      startLine: noteInfo.lines[0] + 1,
      endLine: noteInfo.lines[noteInfo.lines.length - 1] + 1,
      maxLineNumber: this.cm.getValue().split('\n').length,
      editorId: Math.floor(Math.random() * 1000),
      noteNumber: thisNoteNumber,
      maxNoteNumber,
    };

    const processedNoteData = Object.assign({}, data, noteInfo);
    processedNoteData.content = processedNoteData.content.replace(/\\n/g, '\n');

    const $tmpElement = $(document.createElement('div'));
    $tmpElement.html(this.template(processedNoteData));
    const $modal = $tmpElement.children('.modal');

    $modal.find('.btn-delete').on('click', e => {
      e.preventDefault();
      this.deleteNote(thisNoteNumber);
      $modal.modal('hide');
    });

    const $form = $modal.find('form');
    $form.on('submit', e => {
      e.preventDefault();
      const formData = {};

      $form.find('input,textarea').each((inputIndex, inputItem) => {
        const { name } = inputItem;
        let { value } = inputItem;

        if (inputItem.type === 'checkbox') {
          value = inputItem.checked;
        }

        formData[name] = value;
      });

      this.saveNote(thisNoteNumber, formData);
      $modal.modal('hide');
    });

    $modal.appendTo(document.body);
    $modal.modal();

    $form.find(':input:first').focus();
  }

  /**
   * Get positon and size styles for note edit buttons
   * @param {array} lines
   * @param {number} rightOffset
   * @returns {object} JS-formatted style properties
   */
  getButtonStylesForLineSet(lines, rightOffset = 0) {
    const noteDepth = lines.reduce((a, b) => {
      const numNotesThisLine = this.notesPerLine[b];
      return numNotesThisLine > a ? numNotesThisLine : a;
    }, 0);

    const btnHeight = lines.length * CODEX_NOTE_EDIT_BTN_HEIGHT;

    return {
      right:
        noteDepth * CODEX_NOTE_EDIT_BTN_WIDTH +
        noteDepth * CODEX_NOTE_EDIT_BTN_SPACING +
        rightOffset,
      width: CODEX_NOTE_EDIT_BTN_WIDTH,
      height: btnHeight,
    };
  }
}

const CODEX_NOTES_ACTIVE_CLASS = 'codex-notes-active';
const CODEX_NOTES_NEXT_BTN_QUERY = '.codex-notes-next';

const CODEX_TRACKING_ACTION_SHOW_INTRO = 'codex_viewed_intro';
const CODEX_TRACKING_ACTION_SHOW_NOTES = 'codex_viewed_notes';
const CODEX_TRACKING_ACTION_NEXT_NOTE = 'codex_viewed_note_item';
const CODEX_TRACKING_ACTION_HIDE_NOTES = 'codex_hid_notes';

export class CodexNotesDisplayHandlerBase {
  /**
   * @param {object} opts
   * @param {CodexMirrorState} opts.state
   * @param {JQuery} opts.$container
   */
  constructor({ state, $container }) {
    this.cm = state.cm;
    this.initialValues = {
      code: state.initialCode,
      language: state.initialLanguage,
    };
    this.$container = $container;
    this.$notesWrapper = $container.find('.codex-notes');
    if (!this.$notesWrapper.length) {
      return;
    }

    this.$notes = this.$notesWrapper.find('.note,.note-placeholder');
    this.$language = $container.find('[name=language]');
    this.$disableElements = $container.find('.disable-during-notes');
    this.$showNotesButton = this.$container.find('.codex-show-notes');
    this.$toggleButton = this.$container.find('.codex-expand-toggle');
    this.currentNote = CODEX_NOTE_INDEX_START;
    this.numNotes = this.$notes.length;
    this.existingHighlights = [];
    this.isShowingNotes = false;
    this.isAnimated = true;

    this.readNoteData();
    this.bindEvents();
  }

  run() {
    if (!this.userHidNotes) {
      if (this.userNeedsIntro) {
        this.showIntro();
      } else {
        this.$notesIntro = null;
        this.showNotes();
      }
    }
  }

  /**
   * Track a particular note action (viewed note item, hid notes, etc)
   * @param {string} action
   */
  trackNoteAction(action) {
    let params = { action };

    if (action === CODEX_TRACKING_ACTION_NEXT_NOTE) {
      params = Object.assign({}, params, {
        step_position: this.currentNote + 1,
        is_first_step: this.currentNote === 0,
        is_last_step: this.currentNote === this.numNotes - 1,
        triggered_by: INTERACTION_TYPES.PREV_NEXT_BUTTON,
      });
    }

    track('ui', params);
  }

  /** Convert data from the HTML into a usable format */
  readNoteData() {
    const containerData = this.$container.data();

    // Downloaded courses do not have access to course record info so native clients will
    // inline this data in the template
    let injectedData = {};
    // eslint-disable-next-line no-underscore-dangle
    if (window.__brilliantData__) {
      // eslint-disable-next-line no-underscore-dangle
      injectedData = window.__brilliantData__.codex_records.find(
        item => item.codex_env_id === containerData.codexEnvId,
      );
    }

    this.userNeedsIntro = containerData.showNotesIntro;
    this.codexUpdateUrl = injectedData.codex_update_url || containerData.codexUpdateUrl;
    this.userHidNotes = injectedData.user_hid_notes || containerData.notesUserHid;
    this.largestViewedNoteNumber =
      injectedData.largest_viewed_note_number || containerData.notesLargestViewed;

    this.notes = [];
    this.$notes.each((index, noteElement) => {
      const $note = $(noteElement);
      const noteInfo = $note.data();
      this.notes.push(
        Object.assign(
          {
            $note,
          },
          noteInfo,
        ),
      );

      // pick up where user left off
      if (noteInfo.currentNote && !this.userHidNotes) {
        this.currentNote = index - 1;
      }
    });
  }

  /** Add events for displaying notes */
  bindEvents() {
    this.$notesWrapper.find(CODEX_NOTES_NEXT_BTN_QUERY).on('click', e => {
      e.preventDefault();
      this.nextNote();
    });

    this.$showNotesButton.on('click', e => {
      e.preventDefault();
      e.stopPropagation();
      this.$showNotesButton.blur();
      if (this.isShowingNotes) {
        // showNotes calls nextNote, so decrement the currentNote before hiding to
        // re-view the current note when un-hiding
        this.currentNote--;
        this.hideNotes();
      } else {
        this.showNotes();
      }
    });
  }

  /** Show the notes (note: this does NOT show the intro) */
  showNotes() {
    this.userValues = {
      code: this.cm.getValue(),
      language: this.$language.val(),
    };
    $(document.body).addClass(CODEX_NOTES_ACTIVE_CLASS);
    this.$container.removeClass('expanded');
    this.$toggleButton.removeClass('active');
    this.cm.setValue(this.initialValues.code);
    this.$language.val(this.initialValues.language);
    this.$disableElements.prop('disabled', true);
    this.$showNotesButton.addClass(CLASS_NAME_ACTIVE);
    this.cm.setOption('readOnly', true);
    this.$notesWrapper.removeClass(CLASS_NAME_HIDE);
    this.isShowingNotes = true;
    this.trackNoteAction(CODEX_TRACKING_ACTION_SHOW_NOTES);
    this.nextNote();
  }

  /** Show the (optional) intro slide */
  showIntro() {
    $(document.body).addClass(CODEX_NOTES_ACTIVE_CLASS);
    this.$container.removeClass('expanded');
    const $notesIntro = this.$container.find('.codex-notes-intro');
    $notesIntro.removeClass(CLASS_NAME_HIDE).addClass(CLASS_NAME_ANIM_IN);
    $notesIntro.find(CODEX_NOTES_NEXT_BTN_QUERY).on('click', e => {
      e.preventDefault();
      e.stopPropagation();
      $notesIntro.on('transitionend', () => {
        $notesIntro.remove();
        this.showNotes();
      });
      $notesIntro.removeClass(CLASS_NAME_ANIM_IN);
    });
    this.userNeedsIntro = false;
    this.isShowingNotes = true;
    this.trackNoteAction(CODEX_TRACKING_ACTION_SHOW_INTRO);
  }

  /** Hides the notes */
  baseHideNotes() {
    this.clearHighlights();
    this.cm.setOption('readOnly', false);
    this.$disableElements.prop('disabled', false);
    this.$showNotesButton.removeClass(CLASS_NAME_ACTIVE);
    this.cm.setValue(this.userValues.code);
    this.$language.val(this.userValues.language);
    this.userValues = {};
    this.updateCodexRecord({ user_hid_notes: true });
    this.userHidNotes = true;
    this.isShowingNotes = false;
    this.trackNoteAction(CODEX_TRACKING_ACTION_HIDE_NOTES);

    const handleTransition = () => {
      this.$notesWrapper.addClass(CLASS_NAME_HIDE);
      $(document.body).removeClass(CODEX_NOTES_ACTIVE_CLASS);
    };

    if (this.isAnimated) {
      this.$notesWrapper.one('transitionend', handleTransition);
      this.$notesWrapper.removeClass(CLASS_NAME_ANIM_IN);
    } else {
      handleTransition();
    }
  }

  /** Override this in subclasses for a different hideNotes behavior */
  hideNotes() {
    this.baseHideNotes();
  }

  /**
   * Handle continuing to the next note. If the current note is the last
   * note, this will hide notes
   */
  baseNextNote() {
    this.currentNote++;
    if (this.currentNote === this.numNotes) {
      this.currentNote = CODEX_NOTE_INDEX_START;
      this.hideNotes();
    } else {
      const handleTransition = () => {
        const $currentNote = this.$notes.eq(this.currentNote);
        this.$notes.removeClass(CLASS_NAME_ACTIVE);
        $currentNote.addClass(CLASS_NAME_ACTIVE);
        this.highlightLines($currentNote.data('lines'));
        this.positionNote($currentNote);
        this.$notesWrapper.addClass(CLASS_NAME_ANIM_IN);

        const thisNoteNum = this.currentNote + 1;
        this.updateCodexRecord({ largest_viewed_note_number: thisNoteNum });
        this.largestViewedNoteNumber = Math.max(this.largestViewedNoteNumber, thisNoteNum);
      };

      if (this.isAnimated && this.$notesWrapper.hasClass(CLASS_NAME_ANIM_IN)) {
        this.$notesWrapper.one('transitionend', handleTransition);
        this.$notesWrapper.removeClass(CLASS_NAME_ANIM_IN);
      } else {
        handleTransition();
      }

      this.trackNoteAction(CODEX_TRACKING_ACTION_NEXT_NOTE);
    }
  }

  /** Override this in subclasses for a different nextNote behavior */
  nextNote() {
    this.baseNextNote();
  }

  /**
   * Send a request to update the codex record in the backend
   * @param {object} postData
   * @param {number} [postData.largest_viewed_note_number]
   * @param {boolean} [postData.user_hid_notes]
   */
  updateCodexRecord(postData) {
    if (this.codexUpdateUrl && this.validateCodexRecordAttrs(postData)) {
      $.ajax({
        url: this.codexUpdateUrl,
        type: 'POST',
        data: JSON.stringify(postData),
        dataType: 'json',
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
        },
      });
    }
  }

  /**
   * Determine if we should make a request to update the codex record
   * @param {object} attrs
   * @param {number} [attrs.largest_viewed_note_number]
   * @param {boolean} [attrs.user_hid_notes]
   * @param {boolean}
   */
  validateCodexRecordAttrs(attrs) {
    // don't send a request if the user has viewed this note
    if (attrs.largest_viewed_note_number) {
      if (attrs.largest_viewed_note_number <= this.largestViewedNoteNumber) {
        return false;
      }
    }
    // don't send a request if the user has already hid notes
    if (attrs.user_hid_notes === true && this.userHidNotes === true) {
      return false;
    }

    return true;
  }

  /** Remove any highlighted lines of code */
  clearHighlights() {
    this.existingHighlights.forEach($h => $h.remove());
    this.existingHighlights = [];
  }

  /**
   * Highlight lines in a range of line numbers
   * @param {array} lines
   */
  highlightLines(lines) {
    const $baseHighlight = $(document.createElement('span'));
    $baseHighlight.addClass('codex-line-highlight');
    $baseHighlight.css({
      height: CODEX_LINE_HEIGHT,
    });

    this.clearHighlights();

    lines.forEach((lineNumber, index) => {
      const $highlight = $baseHighlight.clone();

      if (index === 0) {
        $highlight.addClass('top');
      }
      if (index === lines.length - 1) {
        $highlight.addClass('bottom');
      }

      const scrollIntoView = true;
      this.cm.addWidget(
        {
          line: lineNumber,
          ch: 0,
        },
        $highlight[0],
        scrollIntoView,
      );

      $highlight.css({
        top: parseInt($highlight.css('top'), 10) - CODEX_LINE_HEIGHT,
      });

      this.existingHighlights.push($highlight);
    });
  }

  /**
   * Position a note based on the location of the lines it's targeting
   * @param {JQuery} $currentNote
   */
  positionNote($currentNote) {
    if (isMobile) {
      return;
    }

    const highlightStart = parseInt(this.existingHighlights[0].css('top'), 10);
    const highlightHeight = CODEX_LINE_HEIGHT * this.existingHighlights.length;
    const highlightMidpoint = highlightStart + highlightHeight / 2;

    const noteHeight = $currentNote.outerHeight();

    const containerHeaderHeight = this.$container.find('header').outerHeight();
    const codeContainerHeight = this.$container.find('.codex-form').outerHeight();

    const codeContainerScrollAmount = this.$container.find('.CodeMirror-scroll').scrollTop();

    // don't let the note extend above or below the codex container
    const minmaxPadding = 4;
    const minPosition = 0 + minmaxPadding;
    const maxPosition = codeContainerHeight - noteHeight - minmaxPadding;

    let notePosition =
      highlightMidpoint - noteHeight / 2 + containerHeaderHeight - codeContainerScrollAmount;
    notePosition = Math.min(Math.max(minPosition, notePosition), maxPosition);
    this.$notesWrapper.css({ top: notePosition });
  }
}

export class CodexNotesDisplayHandlerMobile extends CodexNotesDisplayHandlerBase {
  /** Handle showing the next note on mobile */
  nextNote() {
    this.baseNextNote();

    if (this.isShowingNotes) {
      // keep the codex in the window
      setTimeout(() => {
        const $currentNote = $(this.$notes[this.currentNote]);
        const newScrollPos = this.$container.offset().top - $currentNote.outerHeight();

        // determine if the top is cut off the note
        const currentScrollPos = $window.scrollTop();
        const topIsCutOff = newScrollPos < currentScrollPos;

        // determine if bottom is cut off by the bottom of the screen
        const distanceFromTop = this.$container.offset().top - currentScrollPos;
        const containerHeight = this.$container.find('.codex-form').outerHeight();
        const windowHeight = $window.height();
        const distancePastBottom = distanceFromTop + containerHeight - windowHeight;
        const bottomIsCutOff = distancePastBottom > 0;

        // only scroll if necessary
        if (topIsCutOff || bottomIsCutOff) {
          setScrollPosition(newScrollPos);
        }
      }, 1);
    }
  }
}

/**
 * Map of codex env ids to note instances
 * @type {Object.<number, CodexNotesDisplayHandlerAndroid>}
 */
const activeCodexEnvs = {};

/** Initialize the Android.codexTappedNext function */
function initAndroidBridge() {
  if (!window.Android) {
    window.Android = {};
  }
  if (!window.Android.codexTappedNext) {
    window.Android.codexTappedNext = envId => {
      const codexEnv = activeCodexEnvs[envId];
      if (codexEnv) {
        codexEnv.nextNote();
      }
    };
  }
}

export class CodexNotesDisplayHandlerAndroid extends CodexNotesDisplayHandlerBase {
  /** Setup notes for Android */
  constructor({ state, $container }) {
    super({ state, $container });
    this.isAnimated = false;

    this.envId = $container.data('codex-env-id');
    activeCodexEnvs[this.envId] = this;

    initAndroidBridge();
  }

  /** Handle hiding notes on Android */
  hideNotes() {
    this.baseHideNotes();

    try {
      window.Android.codexHideNotes(this.envId);
    } catch (e) {
      throw new Error('Bridge function unavailable: codexHideNotes');
    }
  }

  /** Handle showing the next note on Android */
  nextNote() {
    this.baseNextNote();

    try {
      window.Android.codexShowNote(this.envId, this.currentNote);
    } catch (e) {
      throw new Error('Bridge function unavailable: codexShowNote');
    }
  }
}
